import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { isApiConfigured } from "../auth-config";
import type { StudentCohortAssignmentReleased } from "../auth/api-client";
import { getStudentReleasedAssignments } from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";
import { useStudentCohorts } from "../student/StudentCohortContext";
import { StudentHomeSkeleton } from "../student/StudentHomeSkeleton";
import { formatDueCountdown, formatDueDateShort } from "../student/formatDueCountdown";
import { parseApiInstant } from "../datetime/parseApiInstant";
import { LessonIcon } from "../teacher/LessonIcon";
import { QuizIcon } from "../teacher/QuizIcon";
import { cohortLessonStudentLinkFromAssignment } from "../student/cohortLessonStudentLink";
import { StudentLessonCard } from "../student/StudentLessonCard";
import { TryTeachingUpgrade } from "../student/TryTeachingUpgrade";
import {
  deriveStudentDisplayStatus,
  studentAssignmentOpen,
  studentDisplayStatusLabel,
} from "../student/studentDisplayStatus";

function useNowTick(intervalMs: number, enabled: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!enabled) return;
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs, enabled]);
  return now;
}

function StudentStatusBadge({ a }: { a: StudentCohortAssignmentReleased }) {
  const open = studentAssignmentOpen(a);
  const status = deriveStudentDisplayStatus(a);
  const mod = open ? "open" : "closed";
  return (
    <span className={`student-display-status student-display-status--${mod} student-display-status--${status}`}>
      {studentDisplayStatusLabel(a)}
    </span>
  );
}

function AssignmentOpenBadge({ a }: { a: StudentCohortAssignmentReleased }) {
  if (!a.released) {
    return <span className="student-lesson-availability student-lesson-availability--planned">Planned</span>;
  }
  if (a.closed) {
    return <span className="student-lesson-availability student-lesson-availability--closed">Closed</span>;
  }
  return <span className="student-lesson-availability student-lesson-availability--open">Open</span>;
}

function DueCell({ dueAt, nowMs }: { dueAt: string | null | undefined; nowMs: number }) {
  if (!dueAt) return <span className="muted">No Due Date</span>;
  const countdown = formatDueCountdown(dueAt, nowMs);
  const shortDate = formatDueDateShort(dueAt);
  const past = countdown === "Past due";
  return (
    <div className="student-due-cell">
      {shortDate ? <div className="student-due-date">{shortDate}</div> : null}
      {countdown ? (
        <div className={`student-due-countdown${past ? " student-due-countdown--past" : ""}`}>{countdown}</div>
      ) : null}
    </div>
  );
}

function findLatestIncomplete(
  assignments: StudentCohortAssignmentReleased[]
): StudentCohortAssignmentReleased | null {
  const openReleased = assignments.filter((a) => a.open);
  const incomplete = openReleased.filter((a) => deriveStudentDisplayStatus(a) !== "complete");
  if (incomplete.length === 0) return null;
  const withDue = incomplete.filter((a) => a.dueAt);
  if (withDue.length > 0) {
    return [...withDue].sort(
      (a, b) => (parseApiInstant(a.dueAt)?.getTime() ?? 0) - (parseApiInstant(b.dueAt)?.getTime() ?? 0)
    )[0] ?? null;
  }
  return incomplete[0] ?? null;
}

export default function StudentLessonsPage() {
  const { profile, getBearerToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cohortFromUrl = searchParams.get("cohort")?.trim() ?? "";
  const bearer = getBearerToken();
  const { cohorts, hasCohorts, loading: cohortsLoading, error: cohortError } = useStudentCohorts();
  const [error, setError] = useState<string | null>(null);
  const [pickCohort, setPickCohort] = useState<string>("");
  const [assignments, setAssignments] = useState<StudentCohortAssignmentReleased[]>([]);
  const [loadAssign, setLoadAssign] = useState(false);
  const hasOpenWithDue = assignments.some((a) => a.open && a.dueAt);
  const nowMs = useNowTick(hasOpenWithDue ? 30_000 : 0, hasOpenWithDue);

  useEffect(() => {
    if (profile?.role === "student" && profile.enrollmentSummary === "archived_only") {
      navigate("/no-active-classes", { replace: true });
    }
  }, [profile?.role, profile?.enrollmentSummary, navigate]);

  useEffect(() => {
    setPickCohort((prev) => {
      if (cohortFromUrl && cohorts.some((c) => c.cohortId === cohortFromUrl)) {
        return cohortFromUrl;
      }
      if (prev && cohorts.some((c) => c.cohortId === prev)) return prev;
      return cohorts[0]?.cohortId ?? "";
    });
  }, [cohorts, cohortFromUrl]);

  useEffect(() => {
    if (!bearer || !pickCohort || !isApiConfigured()) {
      setAssignments([]);
      return;
    }
    let cancelled = false;
    setLoadAssign(true);
    void (async () => {
      try {
        const data = await getStudentReleasedAssignments(bearer, pickCohort);
        if (cancelled) return;
        setAssignments(data.assignments);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Could not load assignments");
      } finally {
        if (!cancelled) setLoadAssign(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bearer, pickCohort]);

  const pickMeta = cohorts.find((c) => c.cohortId === pickCohort);

  const progressStats = useMemo(() => {
    const released = assignments.filter((a) => a.released);
    const complete = released.filter((a) => deriveStudentDisplayStatus(a) === "complete").length;
    return { total: released.length, complete };
  }, [assignments]);

  const latestIncomplete = useMemo(() => findLatestIncomplete(assignments), [assignments]);
  const caughtUp = hasCohorts && progressStats.total > 0 && progressStats.complete === progressStats.total;

  if (cohortsLoading && !hasCohorts) {
    return <StudentHomeSkeleton />;
  }

  return (
    <main className="page page-align-start student-page-inner">
      <div className="card card-wide">
        <header className="row-header">
          <div>
            <p className="eyebrow">Student</p>
            {hasCohorts ? (
              <>
                <h1>How you&apos;re doing</h1>
                <p className="lede">Track your progress and jump into your next lesson.</p>
              </>
            ) : (
              <>
                <h1>Welcome, new student!</h1>
                <p className="lede">
                  Your first step is to get a join code from your teacher and join your class cohort.
                </p>
              </>
            )}
          </div>
        </header>

        {error || cohortError ? (
          <div className="warn-banner" role="alert">
            {error ?? cohortError}
          </div>
        ) : null}

        {!hasCohorts ? (
          <section className="panel student-progress-callout student-progress-callout--empty">
            <h2 className="panel-title">Get started</h2>
            <p className="muted">
              You are not in a cohort yet. Ask your teacher for a join code. Once you join, you will
              see assigned lessons here and can create playgrounds for your projects.
            </p>
            <p className="muted small" style={{ marginTop: "0.5rem" }}>
              While you wait, you can explore Python in the demo IDE. It runs in your browser and does
              not require a cohort.
            </p>
            <div className="actions" style={{ marginTop: "0.85rem" }}>
              <Link to="/join" className="btn primary">
                Join a cohort
              </Link>
              <Link to="/ide" className="btn secondary">
                Open demo IDE
              </Link>
            </div>
          </section>
        ) : (
          <>
            <section className="panel student-progress-callout">
              {caughtUp ? (
                <>
                  <h2 className="panel-title">You&apos;re caught up!</h2>
                  <p className="muted">
                    You&apos;ve completed all {progressStats.total} assigned lesson
                    {progressStats.total === 1 ? "" : "s"}. Nice work. Check back when your teacher assigns more.
                  </p>
                </>
              ) : latestIncomplete ? (
                <>
                  <h2 className="panel-title">Up next</h2>
                  <div className="student-up-next-row">
                    {latestIncomplete.itemKind === "quiz" && latestIncomplete.quizId ? (
                      <QuizIcon
                        quizId={latestIncomplete.quizId!}
                        title={latestIncomplete.lessonTitle}
                        iconName={latestIncomplete.iconName}
                        bgColor={latestIncomplete.iconBgColor}
                        fgColor={latestIncomplete.iconFgColor}
                        size={3.25}
                      />
                    ) : (
                      <LessonIcon
                        lessonId={latestIncomplete.lessonId ?? latestIncomplete.assignmentId}
                        title={latestIncomplete.lessonTitle}
                        iconName={latestIncomplete.iconName}
                        bgColor={latestIncomplete.iconBgColor}
                        fgColor={latestIncomplete.iconFgColor}
                        workspaceMode={latestIncomplete.workspaceMode}
                        size={3.25}
                      />
                    )}
                    <div className="student-up-next-copy">
                      <strong>{latestIncomplete.lessonTitle}</strong>
                      <p className="muted small" style={{ margin: "0.25rem 0 0" }}>
                        {latestIncomplete.dueAt ? (
                          <>
                            Due {formatDueDateShort(latestIncomplete.dueAt)} ·{" "}
                            {formatDueCountdown(latestIncomplete.dueAt, nowMs)}
                          </>
                        ) : (
                          "No due date"
                        )}
                      </p>
                      <Link
                        to={cohortLessonStudentLinkFromAssignment(pickCohort, latestIncomplete)}
                        className="btn primary student-lesson-card-btn"
                        style={{ marginTop: "0.65rem", textDecoration: "none", display: "inline-flex" }}
                      >
                        {latestIncomplete.itemKind === "quiz" ? "Open quiz" : "Open lesson"}
                      </Link>
                    </div>
                  </div>
                </>
              ) : progressStats.total === 0 ? (
                <>
                  <h2 className="panel-title">Waiting on lessons</h2>
                  <p className="muted">Your teacher hasn&apos;t assigned any lessons yet. Check back soon.</p>
                </>
              ) : (
                <>
                  <h2 className="panel-title">Keep going</h2>
                  <p className="muted">
                    {progressStats.complete} of {progressStats.total} assigned lesson
                    {progressStats.total === 1 ? "" : "s"} complete.
                  </p>
                </>
              )}
            </section>

            <section className="panel">
              <h2 className="panel-title">Active cohort</h2>
              <label className="field">
                <span className="field-label">Cohort</span>
                <select
                  className="input"
                  value={pickCohort}
                  onChange={(ev) => setPickCohort(ev.target.value)}
                >
                  {cohorts.map((c) => (
                    <option key={c.cohortId} value={c.cohortId}>
                      {c.courseTitle}: {c.label}
                    </option>
                  ))}
                </select>
              </label>
            </section>

            <section className="panel">
              <h2 className="panel-title">
                Lessons for{" "}
                {pickMeta ? `${pickMeta.courseTitle} · ${pickMeta.label}` : "your cohort"}
              </h2>
              {loadAssign ? (
                <p className="muted">Loading lessons…</p>
              ) : assignments.length === 0 ? (
                <p className="muted">No lessons on the cohort plan yet.</p>
              ) : (
                <>
                  <div className="student-lesson-cards" aria-label="Your lessons">
                    {assignments.map((a) => (
                      <StudentLessonCard
                        key={a.assignmentId}
                        assignment={a}
                        cohortId={pickCohort}
                        nowMs={nowMs}
                      />
                    ))}
                  </div>
                  <div className="student-lessons-table-wrap student-lessons-table-wrap--wide">
                  <table className="teacher-table student-lessons-table">
                    <thead>
                      <tr>
                        <th style={{ width: "3rem" }} aria-label="Icon" />
                        <th>Lesson</th>
                        <th>Availability</th>
                        <th>Progress</th>
                        <th>Due</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {assignments.map((a) => {
                        const openUrl = cohortLessonStudentLinkFromAssignment(pickCohort, a);
                        const isQuiz = a.itemKind === "quiz" && a.quizId;
                        const itemKey = isQuiz ? a.quizId! : a.lessonId ?? a.assignmentId;
                        const canOpen = a.open;
                        const canReviewClosed = !isQuiz && a.closed && a.released;
                        return (
                          <tr key={a.assignmentId}>
                            <td>
                              {isQuiz ? (
                                <QuizIcon
                                  quizId={a.quizId!}
                                  title={a.lessonTitle}
                                  iconName={a.iconName}
                                  bgColor={a.iconBgColor}
                                  fgColor={a.iconFgColor}
                                  size={2.5}
                                />
                              ) : (
                                <LessonIcon
                                  lessonId={a.lessonId ?? itemKey}
                                  title={a.lessonTitle}
                                  iconName={a.iconName}
                                  bgColor={a.iconBgColor}
                                  fgColor={a.iconFgColor}
                                  workspaceMode={a.workspaceMode}
                                  size={2.5}
                                />
                              )}
                            </td>
                            <td>
                              <strong>{a.lessonTitle}</strong>
                            </td>
                            <td>
                              <AssignmentOpenBadge a={a} />
                            </td>
                            <td>
                              {a.released ? <StudentStatusBadge a={a} /> : <span className="muted small">-</span>}
                            </td>
                            <td>
                              {a.released ? (
                                a.open ? (
                                  <DueCell dueAt={a.dueAt} nowMs={nowMs} />
                                ) : a.dueAt ? (
                                  <span className="muted small">{formatDueDateShort(a.dueAt)}</span>
                                ) : (
                                  <span className="muted">No Due Date</span>
                                )
                              ) : (
                                <span className="muted small">-</span>
                              )}
                            </td>
                            <td className="teacher-table-actions">
                              {canOpen ? (
                                <Link
                                  to={openUrl}
                                  className="btn primary tight"
                                  style={{ textDecoration: "none" }}
                                >
                                  Open
                                </Link>
                              ) : canReviewClosed ? (
                                <Link
                                  to={openUrl}
                                  className="btn secondary tight"
                                  style={{ textDecoration: "none" }}
                                >
                                  Review
                                </Link>
                              ) : !a.released ? (
                                <span className="student-lesson-hint muted small">Not assigned yet</span>
                              ) : (
                                <span className="muted small">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </section>
          </>
        )}
        <div style={{ marginTop: "1.25rem" }}>
          <TryTeachingUpgrade />
        </div>
      </div>
    </main>
  );
}
